
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Trash2, Search, AlertTriangle, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface CleanupStats {
  success: boolean;
  orphaned_count: number;
  sample_clients: Array<{
    id: string;
    name: string;
    contact_email: string;
    created_at: string;
  }>;
  error?: string;
}

interface CleanupResult {
  success: boolean;
  deleted_count: number;
  deleted_clients: Array<{
    id: string;
    name: string;
    contact_email: string;
    created_at: string;
  }>;
  cleanup_timestamp: string;
  error?: string;
}

// Type guards for safe JSON casting
function isCleanupStats(data: unknown): data is CleanupStats {
  return typeof data === 'object' && data !== null && 'success' in data;
}

function isCleanupResult(data: unknown): data is CleanupResult {
  return typeof data === 'object' && data !== null && 'success' in data && 'deleted_count' in data;
}

export const AdminOrphanedDataTab: React.FC = () => {
  const [isChecking, setIsChecking] = useState(false);
  const [isCleaning, setIsCleaning] = useState(false);
  const [stats, setStats] = useState<CleanupStats | null>(null);
  const [cleanupResult, setCleanupResult] = useState<CleanupResult | null>(null);

  const checkOrphanedData = async () => {
    setIsChecking(true);
    try {
      const { data, error } = await supabase.rpc('get_orphaned_clients_stats');
      
      if (error) throw error;
      
      if (isCleanupStats(data)) {
        setStats(data);
        if (data.success) {
          toast.success(`Found ${data.orphaned_count} orphaned clients`);
        } else {
          toast.error(data.error || 'Failed to check orphaned data');
        }
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error checking orphaned data:', error);
      toast.error('Failed to check orphaned data');
    } finally {
      setIsChecking(false);
    }
  };

  const cleanupOrphanedData = async () => {
    if (!stats?.orphaned_count) {
      toast.error('No orphaned data to clean up');
      return;
    }

    setIsCleaning(true);
    try {
      const { data, error } = await supabase.rpc('cleanup_orphaned_mock_clients');
      
      if (error) throw error;
      
      if (isCleanupResult(data)) {
        setCleanupResult(data);
        if (data.success) {
          toast.success(`Successfully deleted ${data.deleted_count} orphaned clients`);
          // Reset stats after successful cleanup
          setStats(null);
        } else {
          toast.error(data.error || 'Failed to cleanup orphaned data');
        }
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error cleaning up orphaned data:', error);
      toast.error('Failed to cleanup orphaned data');
    } finally {
      setIsCleaning(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Orphaned Data Management</h2>
        <p className="text-muted-foreground">
          Identify and clean up orphaned mock data that may have been created during system testing.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Check for Orphaned Data
          </CardTitle>
          <CardDescription>
            Scan the database for orphaned client records that appear to be mock data
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button 
            onClick={checkOrphanedData} 
            disabled={isChecking}
            className="w-full"
          >
            {isChecking ? 'Checking...' : 'Check for Orphaned Data'}
          </Button>

          {stats && (
            <Alert className={stats.success ? 'border-blue-200' : 'border-red-200'}>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {stats.success ? (
                  <div className="space-y-2">
                    <p><strong>Found {stats.orphaned_count} orphaned clients</strong></p>
                    {stats.sample_clients && stats.sample_clients.length > 0 && (
                      <div>
                        <p className="font-medium">Sample records:</p>
                        <ul className="text-sm text-muted-foreground">
                          {stats.sample_clients.map((client, index) => (
                            <li key={index}>
                              {client.name} ({client.contact_email}) - {new Date(client.created_at).toLocaleDateString()}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : (
                  <p>Error: {stats.error}</p>
                )}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {stats?.success && stats.orphaned_count > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-500" />
              Clean Up Orphaned Data
            </CardTitle>
            <CardDescription>
              Permanently delete orphaned mock data from the database
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="border-amber-200">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <AlertDescription>
                <strong>Warning:</strong> This action will permanently delete {stats.orphaned_count} orphaned client records 
                and all their associated data. This cannot be undone.
              </AlertDescription>
            </Alert>

            <Button 
              onClick={cleanupOrphanedData}
              disabled={isCleaning}
              variant="destructive"
              className="w-full"
            >
              {isCleaning ? 'Cleaning...' : `Delete ${stats.orphaned_count} Orphaned Records`}
            </Button>
          </CardContent>
        </Card>
      )}

      {cleanupResult && (
        <Alert className={cleanupResult.success ? 'border-green-200' : 'border-red-200'}>
          <CheckCircle className="h-4 w-4 text-green-500" />
          <AlertDescription>
            {cleanupResult.success ? (
              <div className="space-y-2">
                <p><strong>Cleanup completed successfully!</strong></p>
                <p>Deleted {cleanupResult.deleted_count} orphaned clients</p>
                <p className="text-sm text-muted-foreground">
                  Cleanup performed at: {new Date(cleanupResult.cleanup_timestamp).toLocaleString()}
                </p>
              </div>
            ) : (
              <p>Cleanup failed: {cleanupResult.error}</p>
            )}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};
